/* eslint-disable no-self-assign */
import courseAPI from '@/api/course'
import setWith from 'lodash/setWith'
import get from 'lodash/get'
import omit from 'lodash/omit'
import cloneDeep from 'lodash/cloneDeep'
import isNumber from 'lodash/isNumber'
import isEmpty from 'lodash/isEmpty'
import queryString from 'query-string'
import moment from 'moment'
import { COURSE_STATUS } from '@/libs/core/constants'

const { COMPLETED, IN_PROGRESS } = COURSE_STATUS

export const INITIAL_STATE = {
  summary: null,
  courseList: [],
  selectedCourse: null,
  watchHistory: {},
  sessionToken: null,
}

export default {
  namespace: 'session',

  state: INITIAL_STATE,

  reducers: {
    init() {
      return { ...INITIAL_STATE }
    },

    setSession(state, { payload }) {
      const { courseInfos: courseList, sessionToken, ...data } = payload

      let completedCount = 0

      courseList.forEach(element => {
        if (element.courseRecordDetailStatus === COMPLETED) {
          completedCount += 1
        }
      })

      const totalCount = courseList.length
      const pendingCount = totalCount - completedCount
      const progress = (completedCount / totalCount) * 100

      const summary = { ...data, completedCount, totalCount, pendingCount, progress }

      return { ...state, summary, courseList, sessionToken }
    },

    setCourse(state, { payload }) {
      const { courseRecordDetailId, courseSrc } = payload || {}
      const { courseList, selectedCourse } = state
      const {
        courseRecordDetailId: selectedCourseId,
        courseSrc: selectedCourseSrc,
        courseLength: selectedCourseLength,
      } = selectedCourse || {}
      const newCourseList = [...courseList]

      const courseIndex = newCourseList.findIndex(
        course => course.courseRecordDetailId === courseRecordDetailId,
      )

      if (courseIndex >= 0) {
        newCourseList[courseIndex] = { ...newCourseList[courseIndex], payload }
      }

      let newSelectedCourse

      if (payload && courseRecordDetailId === selectedCourseId) {
        const parsedSelectedSrc = queryString.parseUrl(selectedCourseSrc)
        const parsedSrc = queryString.parseUrl(courseSrc)

        const selectedExpires = get(parsedSelectedSrc, 'query.Expires')
        const selectedUrl = get(parsedSelectedSrc, 'url')
        const url = get(parsedSrc, 'url')
        const expiresMoment = moment.unix(selectedExpires)
        const compareMoment = moment().add(selectedCourseLength + 10, 'second')

        if (selectedUrl === url && compareMoment.isBefore(expiresMoment)) {
          newSelectedCourse = { ...selectedCourse, ...omit(payload, 'courseSrc') }
        } else {
          newSelectedCourse = payload
        }
      } else {
        newSelectedCourse = payload
      }

      return { ...state, courseList: newCourseList, selectedCourse: newSelectedCourse }
    },

    updateWatchHistory(state, { payload }) {
      const { watchHistory } = state
      const newWatchHistory = cloneDeep(watchHistory)
      const { sessionToken, courseRecordDetailId, watchTime } = payload

      if (sessionToken && courseRecordDetailId && watchTime) {
        setWith(newWatchHistory, `${sessionToken}.${courseRecordDetailId}`, watchTime, Object)
      }

      return { ...state, watchHistory: newWatchHistory }
    },

    removeWatchHistory(state, { payload }) {
      const { watchHistory } = state
      let newWatchHistory = cloneDeep(watchHistory)
      const { sessionToken, courseRecordDetailId } = payload

      if (sessionToken && courseRecordDetailId) {
        setWith(
          newWatchHistory,
          sessionToken,
          omit(get(newWatchHistory, sessionToken, {}), `${courseRecordDetailId}`),
          Object,
        )

        if (isEmpty(get(newWatchHistory, `${sessionToken}`))) {
          newWatchHistory = omit(newWatchHistory, sessionToken)
        }
      }

      return { ...state, watchHistory: newWatchHistory }
    },
  },

  effects: {
    *getSessionSummary({ payload, successCallback, errorCallback }, { call, put }) {
      const { sessionToken } = payload
      const response = yield call(courseAPI.getSessionSummary, payload)
      const { status, data: resData } = response
      let hasError = true

      if (status === 200) {
        const { data, success } = resData

        if (success) {
          yield put({ type: 'session/setSession', payload: { ...data, sessionToken } })
          hasError = false
          successCallback?.()
        }
      }

      if (hasError) {
        errorCallback?.()
      }
    },

    *getCourseDetails({ payload, successCallback, errorCallback }, { call, put }) {
      const response = yield call(courseAPI.getCourseDetails, payload)
      const { status, data: resData } = response
      let hasError = true

      if (status === 200) {
        const { data, success } = resData
        if (success) {
          yield put({ type: 'session/setCourse', payload: data })
          hasError = false
          successCallback?.(data)
        }
      }

      if (hasError) {
        errorCallback?.()
      }
    },

    *updateCourseWatch({ payload, successCallback, errorCallback }, { call, put, select }) {
      const { sessionToken, courseRecordDetailId } = payload
      const response = yield call(courseAPI.updateCourseWatch, payload)
      const { status, data: resData } = response
      let hasError = true

      if (status === 200) {
        const { success } = resData

        if (success) {
          const { selectedCourse } = yield select(state => state.session)

          const { data: courseData } = payload
          yield put({ type: 'session/setCourse', payload: { ...selectedCourse, ...courseData } })
          yield put({
            type: 'session/removeWatchHistory',
            payload: { sessionToken, courseRecordDetailId },
          })

          hasError = false
          successCallback?.()
        }
      }

      if (hasError) {
        errorCallback?.()
      }
    },

    *syncWatchHistory({ payload, successCallback, errorCallback }, { put, select }) {
      const { sessionToken, courseRecordDetailId } = payload
      const { watchHistory, selectedCourse } = yield select(state => state.session)

      const watchTimeHistory = get(watchHistory, `${sessionToken}.${courseRecordDetailId}`)
      const currentWatchTime =
        get(selectedCourse, 'courseRecordDetailId', '').toString() === courseRecordDetailId
          ? get(selectedCourse, 'videoWatchTime')
          : undefined

      if (
        isNumber(watchTimeHistory) &&
        isNumber(currentWatchTime) &&
        watchTimeHistory > currentWatchTime
      ) {
        yield put({
          type: 'session/updateCourseWatch',
          payload: {
            sessionToken,
            courseRecordDetailId,
            data: {
              courseRecordDetailStatus: get(
                selectedCourse,
                'courseRecordDetailStatus',
                IN_PROGRESS,
              ),
              videoWatchTime: watchTimeHistory,
            },
          },
          successCallback,
          errorCallback,
        })
      }
    },

    *rateCourse({ payload, successCallback, errorCallback }, { call, put, select }) {
      const response = yield call(courseAPI.rateCourse, payload)
      const { status, data: resData } = response
      let hasError = true

      if (status === 200) {
        const { success, data } = resData
        const { courseSrc, ...others } = data
        const { selectedCourse } = yield select(state => state.session)

        if (success) {
          yield put({ type: 'session/setCourse', payload: { ...selectedCourse, ...others } })
          hasError = false
          successCallback?.()
        }
      }

      if (hasError) {
        errorCallback?.()
      }
    },
  },
}
